import {
  DrawerBackdrop,
  DrawerContent as DrawerContentBase,
  DrawerPositioner,
  DrawerRoot,
  Portal,
} from '@chakra-ui/react'
import { clsx } from 'clsx'
import { forwardRef, type ComponentProps, type ComponentType, type ReactNode } from 'react'

type OpenChangeDetails = {
  open: boolean
}

interface SheetProperties {
  [key: string]: unknown
  children: ReactNode
  onOpenChange?: (open: boolean) => void
}

type SheetContentProperties = ComponentProps<typeof DrawerContentBase>

// DrawerContent produces a union type too complex for TS to represent when
// strictTokens is enabled (TS2590). Cast to a looser signature that still
// preserves prop forwarding at runtime.
const DrawerContent = DrawerContentBase as unknown as ComponentType<SheetContentProperties>

export const Sheet = ({ onOpenChange, ...properties }: SheetProperties) => {
  /* --- Section 6: Render --- */
  return (
    <DrawerRoot
      {...properties}
      placement="bottom"
      onOpenChange={(details: OpenChangeDetails) => {
        onOpenChange?.(details.open)
      }}
    />
  )
}

export const SheetContent = forwardRef<HTMLDivElement, SheetContentProperties>(
  ({ children, className, ...properties }, reference) => {
    /* --- Section 6: Render --- */
    return (
      <Portal>
        <DrawerBackdrop bg="var(--overlay-ink-55)" />
        <DrawerPositioner>
          <DrawerContent
            ref={reference}
            bg="var(--theme-card-background)"
            borderTopRadius="[32px]"
            boxShadow="var(--shadow-sheet)"
            className={clsx('ui-sheet-content', typeof className === 'string' ? className : undefined)}
            h="[auto]"
            maxH="[calc(80dvh - env(safe-area-inset-bottom))]"
            maxW="[none]"
            mt="[auto]"
            overflowY="[auto]"
            p="[28px]"
            pb="[calc(28px + env(safe-area-inset-bottom))]"
            w="[100%]"
            {...properties}
          >
            {children}
          </DrawerContent>
        </DrawerPositioner>
      </Portal>
    )
  }
)

SheetContent.displayName = 'SheetContent'
